import { useEffect, useState } from "react";

import Constants from "expo-constants";
import {
  StyleProp,
  ImageStyle,
  Image as NativeImage,
  View,
  ViewStyle,
} from "react-native";
import { ActivityIndicator, Avatar, useTheme } from "react-native-paper";
import { IconSource } from "react-native-paper/lib/typescript/components/Icon";
import { useTailwind } from "tailwind-rn";

const DEFAULT_SIZE = 60;
// we fetch 200x200 images both here and in web app
const DEFAULT_RESIZE = 200;
const RESIZER_URL =
  Constants.expoConfig && Constants.expoConfig.extra?.imagesUrl;

type Props = {
  src?: string;
  base64?: string;
  placeholder?: IconSource;
  size?: number;
  resize?: number;
  style?: StyleProp<ImageStyle>;
};

// TODO: refactor to use expo-image
export default function Image({
  src,
  base64,
  placeholder = "image",
  size = DEFAULT_SIZE,
  resize = DEFAULT_RESIZE,
  style,
}: Props) {
  const { colors } = useTheme();
  const tailwind = useTailwind();

  const [loaded, setLoaded] = useState(true);
  const [loading, setLoading] = useState(false);
  const { debouncedValue: debouncedLoading } = useDebounce(loading, 1000);

  const uri = getImageURI({ src, base64, resize });
  const base64uri = getBase64URI(base64);

  const placeholderStyle = [{ backgroundColor: colors.surfaceDisabled }, style];

  const sizeStyle = { height: size, width: size };

  const showSpinner = debouncedLoading || !src;

  return uri ? (
    <View style={[tailwind("relative"), sizeStyle]}>
      {loaded && (
        <NativeImage
          source={{ uri }}
          style={[tailwind("rounded-full"), style, sizeStyle]}
          onLoadStart={() => setLoading(true)}
          onLoadEnd={() => setLoading(false)}
          onError={() => {
            setLoaded(false);
            setTimeout(() => setLoaded(true), 1000);
          }}
          loadingIndicatorSource={base64 ? { uri: base64uri } : undefined}
        />
      )}
      {showSpinner && (
        <View style={[tailwind("absolute z-50")]}>
          <LoadingImage size={size} style={placeholderStyle} />
        </View>
      )}
    </View>
  ) : (
    <Avatar.Icon
      size={size}
      icon={placeholder}
      style={placeholderStyle}
      color={colors.onSurfaceDisabled}
    />
  );
}

type ImageURIParams = { src?: string; base64?: string; resize?: number };
function getImageURI({ src, base64, resize }: ImageURIParams) {
  if (src && RESIZER_URL) {
    // use image resizer if possible
    return `${RESIZER_URL}/?url=${src}&w=${resize}&h=${resize}`;
  } else if (src) {
    // fallback to regular source
    return src;
  } else {
    // fallback to base64
    return getBase64URI(base64);
  }
}

function getBase64URI(base64: string | undefined) {
  if (!base64) return undefined;

  return `data:image/png;base64,${base64}`;
}

const LoadingImage = ({
  size,
  style,
}: {
  size: number;
  style?: StyleProp<ViewStyle>;
}) => {
  const tailwind = useTailwind();
  const { colors } = useTheme();
  return (
    <View
      style={[
        { height: size, width: size },
        tailwind("items-center justify-center rounded-full"),
        style,
      ]}
    >
      <ActivityIndicator
        style={[
          { backgroundColor: colors.surfaceDisabled },
          tailwind("p-3 rounded-md"),
        ]}
        color={colors.onSurfaceDisabled}
        size={24}
      />
    </View>
  );
};

function useDebounce<T>(value: T, delayMs = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delayMs]);

  function setDebouncedValueInstant(v: T) {
    setDebouncedValue(v);
  }

  return { debouncedValue, setDebouncedValueInstant };
}
